import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import ReactDOM from 'react-dom';
import classNames from 'classnames';

import { FormattedMessage } from '../../../../../util/reactIntl';

import { ExternalLink, NamedLink } from '../../../../../components';

import css from './PriorityLinks.module.css';
import CommonNoShippingModal from '../../../../../components/CommonNoShippingModal/CommonNoShippingModal';

/**
 * Create component that shows only a single "Post a new listing" link.
 *
 * @param {*} props contains customLinksMenuClass
 * @returns div with only one link inside.
 */
export const CreateListingMenuLink = props => {
  return (
    <div className={props.customLinksMenuClass}>
      <NamedLink name="NewListingPage" className={classNames(css.priorityLink, css.highlight)}>
        <span className={css.priorityLinkLabel}  >
          <FormattedMessage id="TopbarDesktop.createListing" />
        </span>
      </NamedLink>
    </div>
  );
};

/**
 * Link component that can be used on TopbarDesktop.
 *
 * @param {*} props containing linkConfig including resolved 'route' params for NamedLink.
 * @returns NamedLink or ExternalLink component based on config.
 */

const PriorityLink = ({ linkConfig, currentUser }) => {
  const history = useHistory();
  const [isOpen, setOpen] = useState(false)
  const { text, type, href, route, highlight } = linkConfig;
  const classes = classNames(css.priorityLink, { [css.highlight]: highlight });

  const hasAddressSaved = !!currentUser?.attributes?.profile?.protectedData?.ResidentialAddress;



  const handleClick = (e) => {
    if (!hasAddressSaved) {
      e.preventDefault();

      const goToSettings = window.confirm(
        'You must add your address before proceeding. Would you like to go to your account settings now?'
      );

      if (goToSettings) {
        history.push('/account-settings'); // replace with your actual account settings route
      }
    }
  };

  if (type === 'internal' && route) {
    const { name, params, to } = route || {};
    return (
      <>
        {currentUser && !hasAddressSaved && linkConfig?.text === "List Item" ? (
          <button
            className={classes}
            onClick={() => {
              setOpen(true)
            }}
          >
            <span className={css.priorityLinkLabel}>{text}</span>
          </button>
        ) : (
          <NamedLink
            name={name}
            params={params}
            to={to}
            className={classes}
            onClick={handleClick}
          >
            <span className={css.priorityLinkLabel}> {text}</span >
          </NamedLink >
        )}
        <CommonNoShippingModal
          isOpen={isOpen}
          onClose={setOpen}
          close={() => setOpen(false)}
          title="Address Required"
          subTitle="To continue listing your products to lend, please add your address. This ensures we connect you with borrowers in your area."
          btnText="Add Address"
          redirect={() => {
            setOpen(false);
            history.push('/account/residential-address?from=topbar'); // or wherever it's appropriate
          }}
        />
      </>

    );
  }

  return (
    <ExternalLink href={href} className={classes} onClick={handleClick}>
      <span className={css.priorityLinkLabel}>{text}</span>
    </ExternalLink>
  );
};


/**
 * Create priority links, which are visible on the desktop layout on the Topbar.
 * If space is limited, this doesn't include anything to the Topbar.
 *
 * @param {*} props contains links array and setLinks function
 * @returns container div with priority links included.
 */
const PriorityLinks = props => {
  const containerRef = useRef(null);

  // With this useEffect, we measure the widths of each rendered priority link
  // This is done once before the real rendering and it's done outside the viewport.
  useEffect(() => {
    const isMeasured = props.links?.[0]?.width;
    if (containerRef.current && !isMeasured) {
      const linksFromRenderedWrapper = [...containerRef.current.childNodes];
      let cumulatedWidth = 0;
      // Generate an array of link configs with width & cumulatedWidth included
      const linksWithWidths = props.links.reduce((links, l, i) => {
        const width = linksFromRenderedWrapper[i].offsetWidth;
        cumulatedWidth = cumulatedWidth + width;
        return [...links, { ...l, width, cumulatedWidth }];
      }, []);
      props.setLinks(linksWithWidths);
    }
  }, [containerRef]);

  const { links, priorityLinks, currentUser } = props;
  const isServer = typeof window === 'undefined';
  const isMeasured = links?.[0]?.width && (priorityLinks.length === 0 || priorityLinks?.[0]?.width);
  const styleWrapper = !!isMeasured
    ? {}
    : {
      style: {
        position: 'absolute',
        top: '-2000px',
        left: '-2000px',
        width: '100%',
        height: 'var(--topbarHeightDesktop)',
        display: 'flex',
        flexDirection: 'row',
      },
    };
  const linkConfigs = isMeasured ? priorityLinks : links;

  return isMeasured || isServer ? (
    <div className={css.priorityLinkWrapper} {...styleWrapper} ref={containerRef}>
      {linkConfigs.map((linkConfig, index) => {
        return <PriorityLink key={`${linkConfig.text}_${index}`} linkConfig={linkConfig} currentUser={currentUser} />;
      })}
    </div>
  ) : (
    ReactDOM.createPortal(
      <div className={css.priorityLinkWrapper} {...styleWrapper} ref={containerRef}>
        {linkConfigs.map((linkConfig, index) => {
          return <PriorityLink key={`${linkConfig.text}_${index}`} linkConfig={linkConfig} currentUser={currentUser} />;
        })}
      </div>,
      document.body
    )
  );
};

export default PriorityLinks;
